import { Box, Typography, Button, IconButton, Stack } from "@mui/material";
import { useNavigate, useLocation } from "react-router-dom";
import { ChevronLeft, Heart } from "lucide-react";
import { useEffect, useState } from "react";
import { notification } from "antd";

import IllustrationHeader from "../../assets/Idea/Idea1.png";
import IllustrationBody from "../../assets/Idea/Idea2.png";
import ideasServices from "../../services/ideasServices";
import "./IdeaInformation.scss";

export default function IdeaInformation() {
	const location = useLocation();
	const navigator = useNavigate();
	const [idea, setIdea] = useState<any>({});
	const [authors, setAuthors] = useState([]);
	const [areas, setAreas] = useState([]);
	const [status, setStatus] = useState("");
	const [hospitals, setHospitals] = useState([]);
	const [isLoading, setIsLoading] = useState(false);
	const [isVoted, setIsVoted] = useState(false);

	const upvote = async (ideaId: number) => {
		ideasServices.upVote(ideaId).then(() =>
			notification.success({
				message: "¡Muchas gracias por tu voto!",
			}),
		);
	};

	const deleteVote = async (ideaId: number) => {
		ideasServices.deleteVote(ideaId).then(() =>
			notification.success({
				message: "¡Se eliminó tu voto!",
			}),
		);
	};

	const getStatusColor = (status: string): string => {
		let statusVariant = "";

		switch (status) {
			case "IDEA":
				statusVariant = "blue";
				break;

			case "EN REVISION":
				statusVariant = "yellow";
				break;

			case "DESARROLLO":
				statusVariant = "green";
				break;

			case "IMPLEMENTACION":
				statusVariant = "purple";
				break;

			case "BACKLOG":
				statusVariant = "grey";
				break;

			default:
				statusVariant = "";
		}
		return "idea__card__header__status--" + statusVariant;
	};

	const fetchData = async () => {
		try {
			await ideasServices
				.verifyVote(location.state.idea_id)
				.then((res) => setIsVoted(res));
		} catch (error) {
			console.error(error);
		}
	};

	useEffect(() => {
		const voteStatus = async () => {
			setIsLoading(true);
			await fetchData();
			setIsLoading(false);
		};

		voteStatus();
	}, [location.state.idea_id]);

	useEffect(() => {
		let ideaId = location.state.idea_id;

		ideasServices.getIdea(ideaId).then((res) => {
			setIdea(res);
			setAuthors(res.authors);
			setAreas(res.areas);
			setStatus(res.status);
			setHospitals(res.hospitals_units);
		});
	}, []);

	const handleVote = async (idea: any) => {
		if (isVoted) {
			await deleteVote(idea);
			setIsVoted(false);
		} else {
			await upvote(idea);
			setIsVoted(true);
		}
		await new Promise((res) => setTimeout(res, 1000));
	};

	return !isLoading ? (
		<Box className="idea">
			<Box className="idea__background">
				<img
					className="idea__background__header"
					src={IllustrationHeader}
					alt=""
				/>

				<img
					className="idea__background__body"
					src={IllustrationBody}
					alt=""
				/>
			</Box>

			<Button onClick={() => navigator(-1)}>
				<ChevronLeft />
				Regresar
			</Button>

			<Box className="idea__card">
				<Box className="idea__card__header">
					<Typography
						className={`idea__card__header__status ${getStatusColor(status)}`}
					>
						{status
							.toLowerCase()
							.split(" ")
							.map((word) => word.charAt(0).toUpperCase() + word.slice(1))
							.join(" ")}
					</Typography>

					{!isVoted ? (
						<Stack flexDirection={"row"}>
							<Stack
								textAlign={"end"}
								justifyContent={"center"}
							>
								<Typography>¿Te agrada esta idea?</Typography>
								<Typography>¡Dale "Me gusta"!</Typography>
							</Stack>
							<IconButton onClick={() => handleVote(idea["idea_id"])}>
								<Heart />
							</IconButton>
						</Stack>
					) : (
						<Stack flexDirection={"row"}>
							<Stack
								textAlign={"end"}
								justifyContent={"center"}
							>
								<Typography>¡Ya votaste por esta idea!</Typography>
								<Typography>Oprime para eliminar tu voto</Typography>
							</Stack>
							<IconButton onClick={() => handleVote(idea["idea_id"])}>
								<Heart fill="white" />
							</IconButton>
						</Stack>
					)}
				</Box>

				<Box className="idea__card__body">
					<Typography>{idea["title"]}</Typography>

					<Box className="idea__card__body__params">
						<Box className="idea__card__body__params__subtitle">
							<Typography>Unidad:</Typography>
							<Typography>Área(s):</Typography>
							<Typography>Categoría:</Typography>
							<Typography>Autor(es):</Typography>
						</Box>

						<Box className="idea__card__body__params__info">
							<Typography>
								{hospitals
									.map((hospital) => `${hospital["name"]}`)
									.join(", ")}
							</Typography>

							<Typography>
								{areas.map((area) => `${area["area_name"]}`).join(", ")}
							</Typography>

							<Typography>
								{idea.category_name === null ? "-" : idea.category_name}
							</Typography>

							<Typography>
								{authors
									.map(
										(author) =>
											`${author["first_name"]} ${author["last_name"]}`,
									)
									.join(", ")}
							</Typography>
						</Box>
					</Box>

					<Box className="idea__card__body__description">
						<Typography>{idea.description}</Typography>
					</Box>

					<Box className="idea__card__body__resources">
						<Typography>Recursos necesarios:</Typography>
						<Typography>
							{idea.resources === null || idea.resources === undefined
								? "-"
								: idea.resources}
						</Typography>
					</Box>
				</Box>
			</Box>
		</Box>
	) : (
		<Box className="loader-overlay">
			<Box className="loader-container">
				<Box className="custom-loader" />
			</Box>
		</Box>
	);
}
